import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useAuth } from "../../contexts/AuthContext";
import { FONT_FAMILY_SERIF, FONT_FAMILY_SCRIPT } from "../../theme";
import mariahBlueLogo from "../../assets/Main Logo (1).svg";
import { trackNavigationClick } from "../../utils/analytics";

interface HeaderProps {
  onMenuClick: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      setScrolled(isScrolled);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const navLinks = [
    { label: "About", path: "/about" },
    { label: "Connect", path: "/contact" },
  ];

  // Add Admin link if user is authenticated
  if (isAuthenticated && user) {
    navLinks.push({ label: "Admin", path: "/admin" });
  }

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: scrolled
          ? "rgba(250, 250, 250, 0.8)"
          : "rgba(250, 250, 250, 0.95)",
        backdropFilter: scrolled ? "blur(10px)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(10px)" : "none",
        borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
        transition: "background-color 0.3s ease, backdrop-filter 0.3s ease",
      }}
    >
      <Toolbar
        sx={{
          height: "80px",
          px: { xs: "20px", md: "60px" },
          justifyContent: "space-between",
        }}
      >
        {/* Logo */}
        <Box
          component={Link}
          to="/"
          sx={{
            display: "flex",
            alignItems: "center",
            textDecoration: "none",
            transition: "opacity 0.2s ease",
            "&:hover": {
              opacity: 0.7,
            },
          }}
        >
          <Box
            component="img"
            src={mariahBlueLogo}
            alt="Mariah Blue"
            sx={{
              height: { xs: "180px", md: "220px" },
              width: "auto",
              pointerEvents: "none", // Allow clicks to pass through to elements behind
            }}
          />
        </Box>

        {/* Desktop Navigation */}
        {!isMobile && (
          <Box
            component="nav"
            sx={{
              display: "flex",
              gap: "40px",
              alignItems: "center",
            }}
          >
            {navLinks.map((link) => (
              <Typography
                key={link.path}
                component={Link}
                to={link.path}
                onClick={() => trackNavigationClick(link.label, link.path)}
                aria-current={isActive(link.path) ? "page" : undefined}
                sx={{
                  fontFamily: '"Playfair Display", serif',
                  fontSize: "0.875rem", // 14px
                  fontWeight: 400,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: isActive(link.path)
                    ? "text.primary"
                    : "text.secondary",
                  textDecoration: "none",
                  position: "relative",
                  transition: "color 0.2s ease",
                  "&:hover": {
                    color: "text.primary",
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: "-4px",
                    left: 0,
                    width: isActive(link.path) ? "100%" : "0%",
                    height: "1px",
                    backgroundColor: "text.primary",
                    transition: "width 0.3s ease",
                  },
                  "&:hover::after": {
                    width: "100%",
                  },
                }}
              >
                {link.label}
              </Typography>
            ))}
          </Box>
        )}

        {/* Mobile Menu Button */}
        {isMobile && (
          <IconButton
            edge="end"
            color="inherit"
            aria-label="menu"
            onClick={onMenuClick}
            sx={{
              color: "text.primary",
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
      </Toolbar>
    </AppBar>
  );
};
